import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import rehypeHighlight from 'rehype-highlight';
import rehypeExternalLinks from 'rehype-external-links';
import rehypeClassNames from 'rehype-class-names';
import rehypeStringify from 'rehype-stringify';

const MAX_CONTENT_LENGTH = 100000;

export const generateMdx = (content: string) => {
  try {
    let processedContent = content;
    let truncated = false;

    if (content.length > MAX_CONTENT_LENGTH) {
      processedContent = content.substring(0, MAX_CONTENT_LENGTH);
      truncated = true;
    }

    const file = unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkRehype, { allowDangerousHtml: true })
      .use(rehypeSlug)
      .use(rehypeAutolinkHeadings)
      .use(rehypeExternalLinks, { target: '_blank', rel: ['noopener', 'noreferrer'] })
      .use(rehypeHighlight)
      .use(rehypeClassNames, {
        h1: 'text-xl font-medium',
        h2: 'text-lg font-medium',
        p: 'text-md py-2',
        ul: 'px-4 py-2 list-disc list-outside',
        ol: 'px-4 py-2 list-decimal list-outside',
        a: 'text-blue-500 hover:underline',
        pre: 'whitespace-pre-wrap text-gray-500',
        code: 'break-words',
        blockquote: 'px-4 italic font-semibold',
        table: 'border-separate border-spacing-4'
      })
      .use(rehypeStringify, { allowDangerousHtml: true })
      .processSync(processedContent);

    let result = String(file);

    if (truncated) {
      result += `<div class="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
        <strong>Note:</strong> Content truncated to ${MAX_CONTENT_LENGTH.toLocaleString()} characters for display.
        Full content is ${content.length.toLocaleString()} characters.
      </div>`;
    }

    return result;
  } catch (error) {
    console.error('Error processing markdown:', error);
    const escapedContent = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>');

    return `<div class="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
      <strong>Error:</strong> Unable to process markdown content. Displaying as plain text.
    </div>
    <pre class="whitespace-pre-wrap text-gray-500">${escapedContent}</pre>`;
  }
}